import { type Aircraft, cruiseSpeed } from "../aircraft/Aircraft";
import { EPSILON, angleTo, distance } from "./geometry";

/**
 * Advance an aircraft along its path.
 *
 * Movement is arc-length exact: the remaining travel budget is consumed
 * waypoint by waypoint, so frame rate never changes the ground track. With an
 * empty path the aircraft keeps flying straight ahead on its current heading.
 *
 * @returns true when the path was fully consumed.
 */
export function advanceAlongPath(
  aircraft: Aircraft,
  deltaSeconds: number,
  speed: number = cruiseSpeed(aircraft),
): boolean {
  let remaining = speed * deltaSeconds;

  while (remaining > EPSILON) {
    if (aircraft.path.length === 0) {
      aircraft.pos.x += Math.cos(aircraft.heading) * remaining;
      aircraft.pos.y += Math.sin(aircraft.heading) * remaining;
      break;
    }

    const waypoint = aircraft.path[0]!;
    const legLength = distance(aircraft.pos, waypoint);
    aircraft.heading = angleTo(aircraft.pos, waypoint);

    if (legLength <= EPSILON) {
      aircraft.path.shift();
      continue;
    }

    if (legLength <= remaining) {
      aircraft.pos.x = waypoint.x;
      aircraft.pos.y = waypoint.y;
      aircraft.path.shift();
      remaining -= legLength;
      continue;
    }

    aircraft.pos.x += Math.cos(aircraft.heading) * remaining;
    aircraft.pos.y += Math.sin(aircraft.heading) * remaining;
    break;
  }

  return aircraft.path.length === 0;
}
